import { createFileRoute, Navigate } from '@tanstack/react-router';
import { SpotterEmbed as SpotterEmbedType } from '@thoughtspot/visual-embed-sdk';
import { HostEvent, SpotterEmbed, useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { RefObject, useCallback, useEffect } from 'react';
import { AdvancedGuard } from '@/components/AdvancedGuard/AdvancedGuard';
import PageNotFound from '@/components/Error/PageNotFound/PageNotFound';
import { FeatureFlagGuard } from '@/components/FeatureFlagGuard/FeatureFlagGuard';
import { FEATURE_FLAGS } from '@/config/features';
import { PAGE_IDS } from '@/constants/pages';
import { spotterConfig } from '@/customizations/thoughtspotCustomizations';

type RouteSearch = {
  reload?: number;
};

/**
 * PRIMARY ROUTE: Spark (ThoughtSpot Spotter)
 *
 * AppEmbed's `Page` enum has no Spotter entry, so Spotter cannot be reached through the
 * shared prerendered embed pattern used by other ThoughtSpot routes. This mirrors the
 * `search` route instead: a direct, non-prerendered SpotterEmbed with its own config.
 *
 * URL Pattern: /dashboard/spark
 */
export const Route = createFileRoute('/dashboard/_primary-routes/spark')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): RouteSearch => ({
    reload: search.reload ? (search.reload as number) : undefined,
  }),
});

function RouteComponent() {
  const ref = useEmbedRef() as RefObject<SpotterEmbedType>;
  const { reload } = Route.useSearch();

  const handleReload = useCallback(async () => {
    try {
      await ref.current?.trigger(HostEvent.Reload);
    } catch (error) {
      console.error('Failed to reload:', error);
    }
  }, [ref]);

  useEffect(() => {
    // Trigger reload if specified in URL params
    if (reload) {
      void handleReload();
    }
  }, [reload, handleReload]);

  return (
    <FeatureFlagGuard
      flag={FEATURE_FLAGS.SPARK}
      redirect={
        <Navigate
          to="/dashboard/$page"
          params={{ page: PAGE_IDS.Overview }}
          replace
        />
      }
    >
      <AdvancedGuard fallback={<PageNotFound />}>
        {/* Spark is a general chat surface over the user's data, not scoped to one
            dataset, so it uses the SDK's data-source auto-detection rather than a
            fixed worksheetId. Requires Spotter 3 (auto mode) enabled on the instance. */}
        <SpotterEmbed
          ref={ref}
          className="h-[calc(100vh-var(--dashboard-menu-height))] w-full"
          worksheetId="auto_mode"
          {...spotterConfig}
        />
      </AdvancedGuard>
    </FeatureFlagGuard>
  );
}
